import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { Edges, OrbitControls, OrthographicCamera, PerspectiveCamera, Text, useTexture } from "@react-three/drei";
import { Euler, RectAreaLight, Shape, Texture } from "three";
import type { ElementCoordinateSystem, Point3D } from "./common_types";
import { computeOrientation } from "./transformations";
import { useStoreDispatch, useStoreSelector } from "../../../../../../../hooks/StoreHooks";
import OrientationLabels from "./OrientationLabels";
import { InstallationType, LatoInstallation } from "../forms/Pergola_type";
import { useFinishes } from "../../../../../../providers/finish_provider";
import { useTeli } from "../../../provider/teli_provider";
import { animated, useSpring } from "@react-spring/three";
import { CompleteTenda } from "./chiusure/tendeZip";
import { CompleteVetrataMaia } from "./chiusure/vetrateComponent";
import { ChiusuraType, Fioriera, IlluminationType, LatoSide, LatoType, TeloType } from "../../../../../../models/pergola_configuration";
import { CompleteVetrataAndromeda } from "./chiusure/vetrateAndromeda";
import { PannelliLaterali } from "./estetica/tubolari";
import CameraLogger, { ControlPanel } from "./componeti/button_ui";
import { Prototipo, PrototipoProfondita, ScartoDe, ScartoDeLarghezza } from "./componeti/Lamelle";
import InstallationWalls, { Muro } from "./componeti/Muri";
import ExtraTravi from "./componeti/extraTrave";
import { removeFiorieraTipo, usePergolaConfigurationBuilderActions } from "../../../../../../../store/reducers/pergola_configuration_builder";
import { Pilastro, PilastroCliccabile, PilastroCliccabileIntermedio } from "./componeti/Pilastri";
import { useConfigurationsServicePergola } from "../../../../../../services/pergola_service";
import { getCurrentUser } from "../../../../../../core/common_functions";

// Sezioni standard
type Sezione = { larghezza: number; altezza: number };
export const sezionePilastro: Sezione = { larghezza: 0.135, altezza: 0.135 };
export const sezioneTrave: Sezione = { larghezza: 0.085, altezza: 0.16 };
export const sezioneLamelle: Sezione = { larghezza: 0, altezza: 0 };

// Lati
export type LatoPergola = { direzione: "Retro" | "Fronte" | "Destra" | "Sinistra"; segmento: number; tipoLato: LatoType };
export type LatoPergolaZip = { direzione: "Retro" | "Fronte" | "Destra" | "Sinistra"; textureTelo: Texture };
export type LatoIlluminazioneLed = { direzione: "Retro" | "Fronte" | "Destra" | "Sinistra";tipo: IlluminationType };

const ALTEZZA_PAVIMENTO = 0.05;
const SHOW_AXES_HELPER = process.env.REACT_APP_SHOW_AXES_HELPER === "true";

export const ConditionalAxesHelper: React.FC<{ size?: number }> = ({ size = 0.5 }) => {
  if (!SHOW_AXES_HELPER) return null;
  return <axesHelper args={[size]} />;
};

const Angolare: React.FC<{
  sezionePilastro: Sezione;
  sezioneTrave: Sezione;
  cs: ElementCoordinateSystem;
}> = (props) => {
  const rotation = useMemo(
    () => computeOrientation(props.cs.direction),
    [props.cs.direction]
  );

  const shape = useMemo((): Shape => {
    const shape = new Shape();
    shape.moveTo(0, 0);
    shape.lineTo(props.sezionePilastro.larghezza, 0);
    shape.lineTo(
      props.sezionePilastro.altezza,
      props.sezioneTrave.larghezza
    );
    shape.lineTo(0, 0);
    return shape;
  }, [props.sezionePilastro, props.sezioneTrave]);

  return (
    <mesh
      position={[
        props.cs.org.x,
        props.cs.org.y,
        props.cs.org.z + props.sezioneTrave.altezza / 2,
      ]}
      rotation={[rotation[0], rotation[1], rotation[2]]}
    >
      <extrudeGeometry
        args={[shape, { depth: props.sezioneTrave.altezza, bevelEnabled: false }]}
      />
      <meshStandardMaterial color="yellow" />
      <ConditionalAxesHelper />
    </mesh>
  );
};
const AxisLabels: React.FC = () => {
  if (!SHOW_AXES_HELPER) return null;
  return (
    <>
      <Text position={[2.1, 0, 0.2]} fontSize={0.2} color="red">
        X
      </Text>
      <Text position={[0, 2.1, 0.2]} fontSize={0.2} color="green">
        Y
      </Text>
      <Text position={[0, 0, 2.1]} fontSize={0.2} color="blue">
        Z
      </Text>
    </>
  );
};

const Pavimento: React.FC<{ posizione: Point3D; larghezza: number; lunghezza: number }> = ({
  posizione,
  larghezza,
  lunghezza,
}) => {
  const texture = useTexture("/textures/pavimento.png");
  
  return (
    <mesh
      position={[
        posizione.x,
        posizione.y,
        posizione.z - ALTEZZA_PAVIMENTO / 2,
      ]}
    >
      <boxGeometry args={[larghezza, lunghezza, ALTEZZA_PAVIMENTO]} />
      <meshStandardMaterial map={texture} />
    </mesh>
  );
};
export const Trave: React.FC<{
  sezione: Sezione;
  lunghezza: number;
  cs: ElementCoordinateSystem;
  material: Texture;
}> = (props) => {
  const rotation = useMemo(
    () => computeOrientation(props.cs.direction),
    [props.cs.direction]
  );

  return (
    <mesh
      position={[
        props.cs.org.x,
        props.cs.org.y,
        props.cs.org.z + props.sezione.altezza / 2,
      ]}
      rotation={[rotation[0], rotation[1], rotation[2]]}
    >
      <boxGeometry
        args={[props.sezione.larghezza, props.sezione.altezza, props.lunghezza]}
      />
      <meshStandardMaterial map={props.material} />
      <Edges color="#363636" />
      <ConditionalAxesHelper />
    </mesh>
  );
};

const StrisciaLed: React.FC<{
  position: [number, number, number];
  length: number;
  rotation?: [number, number, number];
  isActive: boolean;
  ledType: IlluminationType | undefined;
}> = ({ position, length, rotation = [0, 0, 0], isActive, ledType }) => {

  const lightColor =
    ledType === IlluminationType.led_caldo ? "#FFE082" :
    ledType === IlluminationType.led_rgb   ? "#FF0000" :
    "#FFFFFF";

  const emissiveColor =
    ledType === IlluminationType.led_caldo ? "#FFE082" :
    ledType === IlluminationType.led_rgb   ? "#FF0000" :
    "#FFFFFF";
  const rectLightRef = useRef<RectAreaLight>(null);

  return (
    <group position={position} rotation={rotation}>
      <group rotation={[0, 0, Math.PI / 2]}>
        {isActive && (
          <rectAreaLight
            ref={rectLightRef}
            width={0.04}                    
            height={length - sezionePilastro.larghezza * 1.8} 
            intensity={100}                  
            color={lightColor}
            position={[0, 0, 0.01]}         
          />
        )}

        {/* Striscia fisica */}
        <mesh castShadow receiveShadow>
          <cylinderGeometry args={[0.007, 0.007, length - sezionePilastro.larghezza * 2, 20, 1]} />
          <meshStandardMaterial
            color={lightColor}
            emissive={emissiveColor}
            emissiveIntensity={isActive ? 600 : 10}
            toneMapped={false}
            roughness={0.1}
            metalness={0.1}
          />
        </mesh>

        {/* Nucleo luminoso interno (glow centrale) */}
        <mesh>
          <cylinderGeometry args={[0.003, 0.003, length - sezionePilastro.larghezza, 12]} />
          <meshBasicMaterial color={emissiveColor} toneMapped={false} />
        </mesh>

      </group>
    </group>
  );
};


const AreaIlluminazione: React.FC<{
  position: [number, number, number];
  length: number;
  rotation?: [number, number, number];
  isSelected: boolean;
  isACtive: boolean;
  ledType: IlluminationType | undefined;
}> = ({ position, length, rotation = [0, 0, 0], isSelected, ledType,isACtive }) => {
  return (
    <>
      {isSelected && (
        <StrisciaLed
          position={position}
          length={length}
          rotation={rotation}
          isActive={isACtive}
          ledType={ledType}
        />
      )}
    </>
  );
};
// Helper: cattura gl/camera/scene dal contesto R3F *dopo* che OrthographicCamera makeDefault ha effetto
const SceneSetup: React.FC<{
  cameraRef: React.MutableRefObject<THREE.OrthographicCamera | null>;
  onReady: () => void;
}> = ({ cameraRef, onReady }) => {
  const { camera } = useThree();
  useEffect(() => {
    cameraRef.current = camera as THREE.OrthographicCamera;
    onReady();
  }, [camera]);
  return null;
};

type PergolaProps = {
  onScreenshotReady?: (fn: () => Promise<void> | void) => void;
};
const PergolaFinal: React.FC<PergolaProps> = ({ onScreenshotReady }) => {
  const { setModuliLarghezza, setModuliProfondita,removePergolaSideSegmentsFrom } = usePergolaConfigurationBuilderActions();
  const pergolaConfiguration = useStoreSelector((store) => store.pergolaConfigurationBuilder);
   const {base } = useStoreSelector((store) => store);
  const currentUser = getCurrentUser(base);
  const pergolaService = useConfigurationsServicePergola();
  useEffect(() => {
  }, [pergolaConfiguration.led]);
  const isLedActive = (direzione: "Retro" | "Fronte" | "Destra" | "Sinistra") => {
    const active = pergolaConfiguration.led.some(
      (led) => {
        const isMatch = led.direzione === direzione && 
                       (led.tipo === IlluminationType.led_freddo || led.tipo === IlluminationType.led_caldo || IlluminationType.led_rgb);
        return isMatch;
      }
    );
    return active;
  };
  // Aggiungi lo stato per l'orientamento delle lamelle
  const [orientamentoLamelle, setOrientamentoLamelle] = useState<"Larghezza" | "Profondità">("Larghezza");
  const altezzaPilastro = ((pergolaConfiguration.configuration?.dimensioni.altezza ?? 2000) / 1000) - sezioneTrave.altezza;
  const larghezzaSoletta = (pergolaConfiguration.configuration?.dimensioni.larghezza ?? 2000) / 1000;
  const installazione = pergolaConfiguration.configuration?.dimensioni.installazione ?? InstallationType.addossata_larghezza;
  const lato = pergolaConfiguration.configuration?.dimensioni.lati ?? [];
  const isAddossato = (direzione: LatoSide["direzione"]) =>
    lato?.some(
      (l) => l.direzione === direzione && l.tipo === LatoInstallation.addossato
  );
  const isLibero = (direzione: "Fronte" | "Retro" | "Destra" | "Sinistra"): boolean => {
    return !isAddossato(direzione);
  };
  const lunghezzaSoletta = (pergolaConfiguration.configuration?.dimensioni.profondita ?? 4000) / 1000;
  const stacco = larghezzaSoletta >4.1 || lunghezzaSoletta >= 7000;
  const altezzaMuro = ((pergolaConfiguration.configuration?.dimensioni.altezza ?? 3000) / 1000) + 0.5;
  const coloreTelaio = pergolaConfiguration.configuration?.aspetto.colore_telaio ?? "9010";
  const coloreLamelle = pergolaConfiguration.configuration?.aspetto.colore_lamelle ?? "9010";
  //MURI EXTRA
  const murolatoA = useMemo(() => {
    const muri = pergolaConfiguration.muri || [];
    const fronteMuro = muri.find(m => m.direzione === "Fronte");
    return fronteMuro ? fronteMuro.altezza / 1000 : 0;
  }, [pergolaConfiguration.muri]);

  const murolatoB = useMemo(() => {
    const muri = pergolaConfiguration.muri || [];
    const retroMuro = muri.find(m => m.direzione === "Sinistra");
    return retroMuro ? retroMuro.altezza / 1000 : 0;
  }, [pergolaConfiguration.muri]);

  const murolatoC = useMemo(() => {
    const muri = pergolaConfiguration.muri || [];
    const sinistraMuro = muri.find(m => m.direzione === "Retro");
    return sinistraMuro ? sinistraMuro.altezza / 1000 : 0;
  }, [pergolaConfiguration.muri]);

  const murolatoD = useMemo(() => {
    const muri = pergolaConfiguration.muri || [];
    const destraMuro = muri.find(m => m.direzione === "Destra");
    return destraMuro ? destraMuro.altezza / 1000 : 0;
  }, [pergolaConfiguration.muri]);
  const muroAngoloAD = useMemo(() => {
    return Math.max(murolatoA, murolatoD);
  }, [murolatoA, murolatoD]);
  const muroAngoloAB = useMemo(() => {
    return Math.max(murolatoA, murolatoB);
  }, [murolatoA, murolatoB]);
  const muroAngoloBC = useMemo(() => {
    return Math.max(murolatoB, murolatoC);
  }, [murolatoB, murolatoC]);
  const muroAngoloCD = useMemo(() => {
    return Math.max(murolatoC, murolatoD);
  }, [murolatoC, murolatoD]);
  //moduli
  let nstacchi = Math.floor(larghezzaSoletta / 4.1);
  const tolleranza = 1e-9;

  if (Math.abs(nstacchi * 4.1 - larghezzaSoletta) > tolleranza) {
      nstacchi += 1;
  }
  let nstacchiy = Math.floor(lunghezzaSoletta / 7) ;
  if (Math.abs(nstacchiy * 7 - lunghezzaSoletta) > tolleranza) {
      nstacchiy += 1;
  }
  const cleanupInvalidSegments = useCallback(() => {
  const maxX = nstacchi > 1 ? nstacchi : 1;
  const maxY = nstacchiy > 1 ? nstacchiy : 1;

  pergolaConfiguration.lati.forEach(lato => {
      const max = (lato.direzione === "Fronte" || lato.direzione === "Retro") ? maxX : maxY;
      if (lato.segmento > max) {
        dispatch(removePergolaSideSegmentsFrom({
          direzione: lato.direzione,
          fromSegmento: lato.segmento,
        }));
      }
    });
  }, [nstacchi, nstacchiy, pergolaConfiguration.lati, removePergolaSideSegmentsFrom]);
  
  //TRAVE EXTRA
  const stepX = larghezzaSoletta / nstacchi;
  const stepY = lunghezzaSoletta / nstacchiy;
  const [needTrave, setNeedTrave] = useState<boolean>(false);
  useEffect(() => {
    const loadTrave = async () => {
      if(pergolaConfiguration.configuration?.configurationId){
      try {
        const res = await pergolaService.getNeedExtra(
            currentUser,
            pergolaConfiguration.configuration?.configurationId,
            stepX*1000,
            stepY*1000
        );

        setNeedTrave(res);
      } catch (err) {
        console.error("Errore getTrave", err);
        setNeedTrave(false);
      }}else{
      
      }
    };

    if (nstacchi > 0 && nstacchiy > 0) {
      loadTrave();
    }
  }, [
    larghezzaSoletta,
    lunghezzaSoletta,
    nstacchi,
    nstacchiy
  ]);
  useEffect(() => {
    cleanupInvalidSegments();
  }, [cleanupInvalidSegments]);
  //SCREENSHOT
  const [isPhotoLoading, setIsPhotoLoading] = React.useState(false);
  const [photoSuccess, setPhotoSuccess] = React.useState(false);
  const glRef = useRef<THREE.WebGLRenderer | null>(null);
  const cameraRef = useRef<THREE.OrthographicCamera | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const takeCombinedScreenshotRef = useRef<() => Promise<void>>();
const takeCombinedScreenshot = (): Promise<void> => {
  return new Promise((resolve) => {
    const gl = glRef.current;
    const camera = cameraRef.current;
    const scene = sceneRef.current;

    if (!gl || !camera || !scene) {
      resolve();
      return;
    }

    // === SALVA STATO ===
    const oldIsDay = isDay;
    const oldLamelle = statoLamelle;
    const oldTenda = statoTenda;
    const oldVetrata = statoVetrata;
    const oldPos = camera.position.clone();
    const oldRot = camera.rotation.clone();
    const oldZoom = camera.zoom;

    setIsDay(true);
    setStatoLamelle(false);
    setStatoTenda(false);
    setStatoVetrata(false);

    // Attendi che React + Three aggiornino realmente la scena
    setTimeout(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
      // === BOUNDING BOX REALE ===
      const box = new THREE.Box3().setFromObject(scene);
      const size = new THREE.Vector3();
      const center = new THREE.Vector3();
      box.getSize(size);
      box.getCenter(center);

      const maxDim = Math.max(size.x, size.y, size.z);
      const distance = maxDim * 1.6;

      const w = gl.domElement.width;
      const h = gl.domElement.height;

      // === CANVAS FINALE ===
      const finalCanvas = document.createElement("canvas");
      finalCanvas.width = w * 2;
      finalCanvas.height = Math.floor(h * 3.2);
      const ctx = finalCanvas.getContext("2d")!;
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);

      // === ZOOM DINAMICO ===
      const margin = 1.25;
      const dynamicZoom = Math.min(
        w / (maxDim * margin),
        h / (maxDim * margin)
      );

      const renderAndDraw = (
        position: THREE.Vector3,
        targetX: number,
        targetY: number,
        label: string,
        rotateRad: number = 0
      ) => {
        camera.position.copy(position);
        camera.lookAt(center);
        camera.zoom = dynamicZoom;
        camera.updateProjectionMatrix();

        gl.render(scene, camera);

        ctx.save();

        if (rotateRad !== 0) {
          ctx.translate(targetX + w / 2, targetY + h / 2);
          ctx.rotate(rotateRad);
          ctx.drawImage(gl.domElement, -w / 2, -h / 2, w, h);
        } else {
          ctx.drawImage(gl.domElement, targetX, targetY, w, h);
        }

        ctx.restore();

        ctx.fillStyle = "#000";
        ctx.font = "bold 36px Arial";
        ctx.textAlign = "center";
        ctx.textBaseline = "top";
        ctx.fillText(label, targetX + w / 2, targetY + 10);
      };

      // Warm-up: precompila tutti i materiali della scena per evitare il primo frame vuoto
      gl.compile(scene, camera);

      // === VISTE ===
      renderAndDraw(
        new THREE.Vector3(center.x, center.y - distance, center.z),
        0,
        0,
        "Fronte"
      );

      renderAndDraw(
        new THREE.Vector3(center.x, center.y + distance, center.z),
        w,
        0,
        "Retro"
      );

      renderAndDraw(
        new THREE.Vector3(center.x - distance, center.y, center.z),
        0,
        h,
        "Sinistra"
      );

      renderAndDraw(
        new THREE.Vector3(center.x + distance, center.y, center.z),
        w,
        h,
        "Destra"
      );

      renderAndDraw(
        new THREE.Vector3(center.x, center.y, center.z + distance),
        w / 2,
        Math.floor(h * 2.05),
        "Alto",
        -Math.PI / 2 // ← 90° antiorario
      );

      // === EXPORT ===
      finalCanvas.toBlob(async (blob) => {
        if (blob && pergolaConfiguration.configuration?.configurationId) {
          const formData = new FormData();
          formData.append("image", blob, "screenshot.jpg");
          try {
            await configurationService.putImageDetail(
              currentUser,
              pergolaConfiguration.configuration.configurationId,
              formData
            );
          } catch (e) {
            console.error("Errore upload screenshot:", e);
          }
        }

        // === RIPRISTINO ===
        setIsDay(oldIsDay);
        setStatoLamelle(oldLamelle);
        setStatoTenda(oldTenda);
        setStatoVetrata(oldVetrata);

        camera.position.copy(oldPos);
        camera.rotation.copy(oldRot);
        camera.zoom = oldZoom;
        camera.updateProjectionMatrix();
        gl.render(scene, camera);

        resolve();
      }, "image/jpeg", 0.95);
      }); // requestAnimationFrame interno
    }); // requestAnimationFrame esterno
    }, 200);
  });
};
// Aggiorna il ref ad ogni render, così SceneSetup usa sempre la versione aggiornata
takeCombinedScreenshotRef.current = takeCombinedScreenshot;

const takeCurrentViewPhoto = (): Promise<void> => {
  setIsPhotoLoading(true);
  setPhotoSuccess(false);
  return new Promise((resolve) => {
    const gl = glRef.current;
    const camera = cameraRef.current;
    const scene = sceneRef.current;

    if (!gl || !camera || !scene) {
      setIsPhotoLoading(false);
      resolve();
      return;
    }

    const w = gl.domElement.width;
    const h = gl.domElement.height;
    const tempCanvas = document.createElement("canvas");
    tempCanvas.width = w;
    tempCanvas.height = h;
    const ctx = tempCanvas.getContext("2d")!;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(gl.domElement, 0, 0);
    tempCanvas.toBlob(async (blob) => {
      if (blob && pergolaConfiguration.configuration?.configurationId) {
        const formData = new FormData();
        formData.append("image", blob, "photo_current.jpg");
        try {
          await configurationService.putImagePdf(
            currentUser,
            pergolaConfiguration.configuration.configurationId,
            formData
          );
          setPhotoSuccess(true);
          setTimeout(() => setPhotoSuccess(false), 2000);
        } catch (e) {
          console.error("Errore upload foto:", e);
        }
      }
      setIsPhotoLoading(false);
      resolve();
    }, "image/jpeg", 0.95);
  });
};

  //GENERAL
  const dimensioneOrientamento = orientamentoLamelle === "Larghezza" ? lunghezzaSoletta : larghezzaSoletta;
  const numeroLamiere = Math.max(0, Math.floor((dimensioneOrientamento - 0.17) / 0.170));
  const residuoLamelle = (dimensioneOrientamento - 0.17) - (numeroLamiere * 0.170);
  const numeroPannelliLaterali = 1;
  const configurationService = useConfigurationsServicePergola();
  //PILASTRI
  const altezzaPilastroH1 =  (pergolaConfiguration.pilastri.find(t => t.direzione === 'H1')?.altezza?? 0)/1000; 
  const altezzaPilastroH2 = (pergolaConfiguration.pilastri.find(t => t.direzione === 'H2')?.altezza?? 0)/1000; 
  const altezzaPilastroH3 = (pergolaConfiguration.pilastri.find(t => t.direzione === 'H3')?.altezza?? 0)/1000; 
  const altezzaPilastroH4 = (pergolaConfiguration.pilastri.find(t => t.direzione === 'H4')?.altezza?? 0)/1000;
  //TUBOLARI
  const numeroPannelliAB = pergolaConfiguration.tubolari.find(t => t.direzione === "Fronte")?.numero ?? 0;
  const numeroPannelliCB = pergolaConfiguration.tubolari.find(t => t.direzione === "Retro")?.numero ?? 0;
  const numeroPannelliBA = pergolaConfiguration.tubolari.find(t => t.direzione === "Sinistra")?.numero ?? 0;
  const numeroPannelliAD = pergolaConfiguration.tubolari.find(t => t.direzione === "Destra")?.numero ?? 0;
  const numeroPannelliAB2 = pergolaConfiguration.tubolari.find(t => t.direzione === "Fronte2")?.numero ?? 0;
  const numeroPannelliCB2 = pergolaConfiguration.tubolari.find(t => t.direzione === "Retro2")?.numero ?? 0;
  const numeroPannelliBA2 = pergolaConfiguration.tubolari.find(t => t.direzione === "Sinistra2")?.numero ?? 0;
  const numeroPannelliAD2 = pergolaConfiguration.tubolari.find(t => t.direzione === "Destra2")?.numero ?? 0;
  //FIORIERE
  const fioriereAttuali = pergolaConfiguration.fioriere;
  const [pilastriSelezionati, setPilastriSelezionati] = useState<Set<string>>(new Set());
  const [pilastroHover, setPilastroHover] = useState<string | null>(null);
  const dispatch = useStoreDispatch();
  useEffect(() => {
    if (nstacchiy < 2 && nstacchi < 2) {
      dispatch(removeFiorieraTipo({ tipo: Fioriera.fioriera_500x500mm }));
    }
  }, [nstacchiy, nstacchi, dispatch]);
  //TEXTURE
  const numeroLamiereCalcolato =
  nstacchi >= 2 && nstacchiy >= 2
    ? numeroLamiere
    : nstacchi >= 2
      ? numeroLamiere + 1
      : numeroLamiere;
  const [texture, setTexture] = useState<Texture | undefined>();
  const [textureLamelle, setTextureLamelle] = useState<Texture | undefined>();
  const [textureTeliMap, setTextureTeliMap] = useState<Record<string, Texture>>({});
  const [textureTelaioMap, setTextureTelaioMap] = useState<Record<string, Texture>>({});
  const [textureTelo, setTextureTelo] = useState<Texture | undefined>();
  const [textureFioriere, setTextureFioriere] = useState<Texture | undefined>();
  const [statoLamelle, setStatoLamelle] = useState<boolean>(false);
  const [statoTenda, setStatoTenda] = useState<boolean>(false);
  const [statoVetrata, setStatoVetrata] = useState<boolean>(false);
  const toggleOrientamento = useCallback(() => {
    setOrientamentoLamelle(prev => prev === "Larghezza" ? "Profondità" : "Larghezza");
  }, []);

  const toggleLamelle = useCallback(() => {
    setStatoLamelle(prev => !prev);
  }, []);
const hasTendaZip = useMemo(() => {
  return pergolaConfiguration.lati?.some(lato => 
    lato?.tipoLato?.tipologia?.tipo_chiusura === ChiusuraType.tende_zip
  ) ?? false;
}, [pergolaConfiguration.lati]);

const hasVetrata = useMemo(() => {
  return pergolaConfiguration.lati?.some(lato => 
    lato?.tipoLato?.tipologia?.tipo_chiusura === ChiusuraType.vetrate_scorrevoli_maia ||
    lato?.tipoLato?.tipologia?.tipo_chiusura === ChiusuraType.vetrate_ad_impacchettamento_andromeda
  ) ?? false;
}, [pergolaConfiguration.lati]);
  const finishes = useFinishes();
  const teliFinishes = useTeli();

  useEffect(() => {
    const loadTeliTextures = async () => {
      const map: Record<string, Texture> = {};

      if (!pergolaConfiguration || !pergolaConfiguration.lati || !Array.isArray(pergolaConfiguration.lati)) {
        console.warn("pergolaConfiguration.lati non valido o mancante:", pergolaConfiguration);
        setTextureTeliMap(map);
        return;
      }

      for (const lato of pergolaConfiguration.lati) {
        if (!lato || !lato.tipoLato) {
          console.warn(`Lato ${lato?.direzione} ha tipoLato mancante, skippo texture loading.`);
          continue;
        }
        const tipologia = lato.tipoLato.tipologia;

        if (!tipologia || typeof tipologia !== 'object') {
          console.warn(`Tipologia mancante o non valida per lato ${lato.direzione}:`, tipologia);
          continue;
        }

        if ('tipo_chiusura' in tipologia && tipologia.tipo_chiusura === ChiusuraType.tende_zip) {
          let codiceTeloToUse: string | undefined;

          if ('tipo_telo' in tipologia && tipologia.tipo_telo === TeloType.teloCristal) {
            codiceTeloToUse = "2020";
          } else if ('codice_telo' in tipologia && tipologia.codice_telo) {
            codiceTeloToUse = tipologia.codice_telo;
          }

          if (codiceTeloToUse && teliFinishes?.createTextureTeli) {
            try {
              const texture = await teliFinishes.createTextureTeli(codiceTeloToUse);
              const identificativo = lato.direzione + lato.segmento; 
              if (texture) {
                map[identificativo] = texture;
              }
            } catch (textureError) {
              console.error(`Errore nel caricamento texture per codice ${codiceTeloToUse} sul lato ${lato.direzione}:`, textureError);
            }
          }
        }
      }

      setTextureTeliMap(map);
    };

    loadTeliTextures();
  }, [pergolaConfiguration.lati, teliFinishes]);
  const fallbackTexture = useMemo(() => {
  const tex = new THREE.Texture();
    tex.image = { width: 1, height: 1 };
    tex.needsUpdate = true;
    return tex;
  }, []);
  const renderPilastriIntermediProfondita = (
  larghezzaSoletta: number,
  lunghezzaSoletta: number,
  altezzaPilastro: number,
  sezionePilastro: Sezione,
  texture: Texture,
  muroAngoloAB: number,
  muroAngoloCD: number,
  nstacchiy: number
) => {
  if (lunghezzaSoletta < 7) return null;

  const step = lunghezzaSoletta / nstacchiy;
  const numPil = Math.floor(lunghezzaSoletta / step) + 2;

  return Array.from({ length: numPil }, (_, i) => {
    if (i === 0 || i === numPil - 1) return null;
    if (i === numPil - 2 && lunghezzaSoletta - (numPil - 2) * step < 2) return null;

    const y = sezioneTrave.larghezza / 2 + i * step - 0.0175;

    const altezzaAB = altezzaPilastro - murolatoB;
    const zAB = altezzaPilastro / 2 + murolatoB / 2;
    const altezzaCD = altezzaPilastro - murolatoD;
    const zCD = altezzaPilastro / 2 + murolatoD / 2;

    // ID pilastri intermedi
    const idSinistra = `pilastro_intermedio_sinistra_y_${i}`;
    const idDestra = `pilastro_intermedio_destra_y_${i}`;

    return (
      <group key={`pilastri-intermedi-y-${i}`}>
        {/* Pilastro intermedio SINISTRA */}
        {isLibero("Sinistra") && (
    <PilastroCliccabileIntermedio
      id={idSinistra}
      altezza={altezzaAB}
      material={texture}
      materialF={textureFioriere ?? fallbackTexture}
      cs={{
        org: {
          x: sezionePilastro.larghezza / 2,
          y,
          z: zAB,
        },
        direction: [0, 0, 1],
      }}
      isSelected={fioriereAttuali.some(f => f.direzione === idSinistra)}
      isHovered={pilastroHover === idSinistra}
      onToggleSelect={(id) =>
        setPilastriSelezionati((prev) => {
          const nuovo = new Set(prev);
          if (nuovo.has(id)) nuovo.delete(id);
          else nuovo.add(id);
          return nuovo;
        })
      }
      onHover={setPilastroHover}
    />
  )
}
        {/* Pilastro intermedio DESTRA */}
        {isLibero("Destra") && (
        <PilastroCliccabileIntermedio
          id={idDestra}
          altezza={altezzaCD}
          material={texture}
          materialF={textureFioriere ?? fallbackTexture}
          cs={{
            org: {
              x: larghezzaSoletta - sezionePilastro.larghezza / 2,
              y,
              z: zCD,
            },
            direction: [0, 0, 1],
          }}
          isSelected={fioriereAttuali.some(f => f.direzione === idDestra)}
          isHovered={pilastroHover === idDestra}
          onToggleSelect={(id) =>
            setPilastriSelezionati((prev) => {
              const nuovo = new Set(prev);
              if (nuovo.has(id)) nuovo.delete(id);
              else nuovo.add(id);
              return nuovo;
            })
          }
          onHover={setPilastroHover}
        />
        )}
      </group>
    );
  }).filter(Boolean);
};
  const getPilastriIntermediPerLato = (
    direzione: "Fronte" | "Retro" | "Destra" | "Sinistra"
  ): { start: number; end: number }[] => {
    const segments: { start: number; end: number }[] = [];

    if (direzione === "Fronte" || direzione === "Retro") {
      if (larghezzaSoletta <= 4.1) return [{ start: 0, end: larghezzaSoletta }];

      const step = larghezzaSoletta / nstacchi;
      const numPil = Math.floor(larghezzaSoletta / step) + 2;

      let currentStart = 0;
      for (let i = 1; i < numPil - 1; i++) {
        const pilastroY = i * step;
        const pilastroSize = sezionePilastro.altezza;
        if (pilastroY - currentStart > pilastroSize * 2) {
          segments.push({
            start: currentStart,
            end: pilastroY - pilastroSize / 2,
          });
        }
        currentStart = pilastroY + pilastroSize / 2;
      }
      
      if (larghezzaSoletta - currentStart > sezionePilastro.altezza) {
        segments.push({
          start: currentStart,
          end: larghezzaSoletta,
        });
      }
    } else if (direzione === "Destra" || direzione === "Sinistra") {
      // Lato lungo X (larghezza)
      if (lunghezzaSoletta <= 7) return [{ start: 0, end: lunghezzaSoletta }];

      const step = lunghezzaSoletta / nstacchiy;
      const numPil = Math.floor(lunghezzaSoletta / step) + 2;

      let currentStart = 0;
      for (let i = 1; i < numPil - 1; i++) {
        const pilastroX = i * step +  sezionePilastro.larghezza * 2;
        const pilastroSize = sezionePilastro.larghezza;

        if (pilastroX - currentStart > pilastroSize * 2) {
          segments.push({
            start: currentStart,
            end: pilastroX - pilastroSize * 2,
          });
        }
        currentStart = pilastroX - sezionePilastro.larghezza * 2;
      }

      if (lunghezzaSoletta - currentStart > sezionePilastro.larghezza) {
        segments.push({
          start: currentStart,
          end: lunghezzaSoletta,
        });
      }
    }

    return segments;
  };
  const init = async () => {
    try {
      const txt = await finishes?.createTexture(coloreTelaio);
      const txtL = await finishes?.createTexture(coloreLamelle);
      const txtT = await teliFinishes?.createTextureTeli("50272");
      const txtF = await finishes?.createTexture("9010");
      setTexture(txt);
      setTextureLamelle(txtL);
      setTextureTelo(txtT);
      setTextureFioriere(txtF);
    } catch (error) {
      console.error("Error loading textures:", error);
    }
  };
  const [isDay, setIsDay] = useState(true);
  useEffect(() => {
    init();
  }, [coloreTelaio, coloreLamelle]);

  if (!texture || !textureLamelle || !textureTelo) {
    console.warn("Textures not loaded, rendering null:", { texture, textureLamelle, textureTelo });
    return null;
  }
  const CameraPositionLogger: React.FC = () => {
    const { camera } = useThree();

    useFrame(() => {
      if (camera instanceof THREE.OrthographicCamera) {
        console.log("Camera position:", camera.position.toArray());
        console.log("Camera rotation:", camera.rotation.toArray());
        console.log("Camera fov:", camera.zoom);
      }
    });
    return null;
  };
  const toggleVetrata = () => {
    setStatoVetrata(prev => !prev);
  };
  const degToRad = (gradi: number): number => gradi * Math.PI / 180;

  const rotazioneLamelle = (angoloGradi: number): number => statoLamelle ? -degToRad(angoloGradi) : 0;

  const toggleTenda = () => {
    setStatoTenda(!statoTenda);
  };

  const ledConfigurations: { direzione: "Retro" | "Fronte" | "Destra" | "Sinistra"; position: [number, number, number]; length: number; rotation: [number, number, number] }[] = [
    {
      direzione: 'Fronte',
      position: [larghezzaSoletta / 2, sezioneTrave.larghezza, altezzaPilastro + sezioneTrave.altezza / 3],
      length: larghezzaSoletta - sezionePilastro.larghezza * 2,
      rotation: [0, 0, 0],
    },
    {
      direzione: 'Retro',
      position: [larghezzaSoletta / 2, lunghezzaSoletta - sezioneTrave.larghezza, altezzaPilastro + sezioneTrave.altezza / 3],
      length: larghezzaSoletta - sezionePilastro.larghezza * 2,
      rotation: [0, 0, 0],
    },
    {
      direzione: 'Destra',
      position: [larghezzaSoletta - sezioneTrave.larghezza, lunghezzaSoletta / 2, altezzaPilastro + sezioneTrave.altezza / 3],
      length: lunghezzaSoletta - sezionePilastro.altezza * 2,
      rotation: [0, 0, Math.PI / 2],
    },
    {
      direzione: 'Sinistra',
      position: [sezioneTrave.larghezza, lunghezzaSoletta / 2, altezzaPilastro + sezioneTrave.altezza / 3],
      length: lunghezzaSoletta - sezionePilastro.altezza * 2,
      rotation: [0, 0, -Math.PI / 2],
    },
  ];
  return (
    <div style={{ position: 'relative', width: '100%', height: window.innerWidth < window.innerHeight ? "400px" : "100%" }}>
      <div style={{ position: 'absolute', top: '20px', left: '20px', zIndex: 1000, display: 'flex', gap: '10px' }}>
        <button
          onClick={() => takeCurrentViewPhoto()}
          disabled={isPhotoLoading}
          style={{
            padding: '8px 14px',
            borderRadius: '8px',
            border: 'none',
            backgroundColor: photoSuccess ? '#15803d' : isPhotoLoading ? '#374151' : '#1e3a8a',
            color: '#fff',
            cursor: isPhotoLoading ? 'not-allowed' : 'pointer',
            fontSize: '13px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            transition: 'background-color 0.3s',
            minWidth: '120px',
          }}
          title="Scatta foto dalla posizione attuale"
        >
          {isPhotoLoading ? '⏳ Invio...' : photoSuccess ? '✅ Salvata!' : '📷 Scatta Anteprima'}
        </button>
      </div>
      {/*<button
        onClick={() => setIsDay(prev => !prev)}
        style={{
          position: 'absolute',
          top: 20,
          right: 20,
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          border: 'none',
          fontSize: '28px',
          backgroundColor: isDay ? '#ffd43b' : '#1e3a8a',
          color: isDay ? '#000' : '#fff',
          cursor: 'pointer',
          boxShadow: '0 6px 20px rgba(0,0,0,0.35)',
          zIndex: 1000,
          transition: 'all 0.4s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        title={isDay ? 'Passa alla notte' : 'Passa al giorno'}
      >
        {isDay ? <LightModeIcon /> : <DarkModeIcon />}
      </button>*/}
      <Canvas
        gl={{ preserveDrawingBuffer: true }}
        onCreated={({ gl, scene }) => {
          glRef.current = gl;
          sceneRef.current = scene;
          // cameraRef viene impostato da SceneSetup dopo che OrthographicCamera makeDefault ha effetto
        }}
      >
        <OrthographicCamera
          makeDefault
          position={[larghezzaSoletta / 2 - 7, lunghezzaSoletta / 2 - 10, altezzaPilastro / 2 + 5]}
          zoom={window.innerWidth < window.innerHeight ? 60 : 100}
          near={0.1}
          far={100}
          up={[0, 0, 1]}
        />
        <SceneSetup
          cameraRef={cameraRef}
          onReady={() => onScreenshotReady?.(() => takeCombinedScreenshotRef.current?.())}
        />
        {/*<PerspectiveCamera
          makeDefault
          position={[-7, -10, 5]}
          fov={50}          
          near={0.1}
          far={100}
          up={[0, 0, 1]}   
        />*/}
        {/*<CameraPositionLogger />*/}
        {isDay ? (
          <>
            <ambientLight intensity={1} />
            <directionalLight position={[10, 10, 10]} intensity={1} />
          </>
        ) : (
          <>
            <ambientLight intensity={0.1} color="#b9d5ff" />
            <directionalLight intensity={1.2} color="#b9d5ff" position={[10, 10, 10]} />
            <color attach="background" args={['#0f172a']} />
          </>
        )}
        <OrbitControls
          enablePan={false}
          target={[0, 0, altezzaPilastro / 2]}
          minPolarAngle={0}
          maxPolarAngle={Math.PI}
          minAzimuthAngle={-Infinity}
          maxAzimuthAngle={Infinity}
        />
        <group>
          <ConditionalAxesHelper size={2} />
          <AxisLabels />
          <Pavimento
            posizione={{ x: 0, y: 0, z: 0 }}
            larghezza={larghezzaSoletta}
            lunghezza={lunghezzaSoletta}
          />
        </group>
        <group position={[-larghezzaSoletta / 2, -lunghezzaSoletta / 2, 0]}>
          <OrientationLabels
            larghezza={larghezzaSoletta}
            lunghezza={lunghezzaSoletta}
            installazione={installazione}
            nstacchiX={nstacchi - 1}    
            nstacchiY={nstacchiy - 1} 
          />
          {renderPilastriIntermediProfondita(
              larghezzaSoletta,
              lunghezzaSoletta,
              altezzaPilastro,
              sezionePilastro,
              texture,
              murolatoB,
              muroAngoloCD,
              nstacchiy
          )}
          {larghezzaSoletta >= 4.1 && (
            (() => {
              const step = larghezzaSoletta / nstacchi;
              const numPil = Math.floor(larghezzaSoletta / step) + 2;

              return Array.from({ length: numPil }, (_, i) => {
                if (i === 0 || i === numPil - 1) return null;
                
                if (i === numPil - 2 && larghezzaSoletta - (numPil - 2) * step < 2) {
                  return null;
                }
                const x = i * step - sezionePilastro.larghezza / 2;
                const idRetro = `pilastro_intermedio_retro_x_${i}`;
                const altezzaAB = altezzaPilastro - murolatoC;
                const zAB = altezzaPilastro / 2 + murolatoC / 2;
                if (!isLibero("Retro")) return null;

                return (
                  <PilastroCliccabileIntermedio
                    id={idRetro}
                    altezza={altezzaAB}
                    material={texture}
                    materialF={textureFioriere ?? fallbackTexture}
                    cs={{
                      org: {
                        x,
                        y: lunghezzaSoletta - sezionePilastro.altezza / 2,
                        z: zAB,
                      },
                      direction: [0, 0, 1],
                    }}
                    isSelected={fioriereAttuali.some(f => f.direzione === idRetro)}
                    isHovered={pilastroHover === idRetro}
                    onToggleSelect={(id) =>
                      setPilastriSelezionati((prev) => {
                        const nuovo = new Set(prev);
                        if (nuovo.has(id)) nuovo.delete(id);
                        else nuovo.add(id);
                        return nuovo;
                      })
                    }
                    onHover={setPilastroHover}
                  />
                );
              }).filter(Boolean);
            })()
          )}
          {larghezzaSoletta >= 4.1 && (() => {
            const step = larghezzaSoletta / nstacchi;
            const numPil = Math.floor(larghezzaSoletta / step) + 2;

            return Array.from({ length: numPil }, (_, i) => {
              if (i === 0 || i === numPil - 1) return null;
              let x = 0;
              if(i === numPil - 2 && (larghezzaSoletta - (numPil - 2)*step < 2)){
                return null
              }else{
                x = i * step - sezionePilastro.larghezza/2;
              }
              const idFronte = `pilastro_intermedio_fronte_x_${i}`;
              if (!isLibero("Fronte")) return null;
              return (
                <PilastroCliccabileIntermedio
                    id={idFronte}
                    materialF={textureFioriere ?? fallbackTexture}
                    altezza={altezzaPilastro - murolatoA}
                    material={texture}
                    cs={{
                      org: {
                        x,
                        y: sezionePilastro.altezza / 2,
                        z: altezzaPilastro / 2 + murolatoA / 2,
                      },
                      direction: [0, 0, 1],
                    }}
                    isSelected={fioriereAttuali.some(f => f.direzione === idFronte)}
                    isHovered={pilastroHover === idFronte}
                    onToggleSelect={(id) =>
                      setPilastriSelezionati((prev) => {
                        const nuovo = new Set(prev);
                        if (nuovo.has(id)) nuovo.delete(id);
                        else nuovo.add(id);
                        return nuovo;
                      })
                    }
                    onHover={setPilastroHover}
                  />
              );
            }).filter(Boolean);
          })()}
          {/*PILASTRI INTERNI*/}
          {larghezzaSoletta >= 4.1 && lunghezzaSoletta >= 7 && (() => {
              const stepX = larghezzaSoletta / nstacchi;
              const stepY = lunghezzaSoletta / nstacchiy;

              const numTraviX = Math.floor(larghezzaSoletta / stepX);
              const numTraviY = Math.floor(lunghezzaSoletta / stepY);

              const incroci: { x: number; y: number }[] = [];
              for (let i = 1; i <= numTraviY; i++) {
                const y = i * stepY;
                if (y <= sezioneTrave.larghezza || y >= lunghezzaSoletta - sezioneTrave.larghezza) continue;

                for (let j = 1; j <= numTraviX; j++) {
                  const x = j * stepX;
                  if (x <= sezioneTrave.larghezza || x >= larghezzaSoletta - sezioneTrave.larghezza) continue;

                  incroci.push({ x, y });
                }
              }

              return incroci.map(({ x, y }, idx) => {
                const muroSinistra = 0; 
                const muroDestra = 0;   
                const muroFronte = 0;   
                const muroRetro = 0;    

                const muroLocale =
                  y < lunghezzaSoletta / 2
                    ? Math.max(muroFronte, x < larghezzaSoletta / 2 ? muroSinistra : muroDestra)
                    : Math.max(muroRetro, x < larghezzaSoletta / 2 ? muroSinistra : muroDestra);

                const altezzaPilastroIncrocio = altezzaPilastro - muroLocale;
                const zCenter = altezzaPilastro / 2 + muroLocale / 2;

                return (
                  <Pilastro
                    key={`pilastro-incrocio-${idx}`}
                    altezza={altezzaPilastroIncrocio}
                    material={texture}
                    cs={{
                      org: {
                        x: x - sezioneTrave.larghezza/2 - 0.02,
                        y: y + sezioneTrave.larghezza/2 - 0.02,
                        z: zCenter,
                      },
                      direction: [0, 0, 1],
                    }}
                  />
                );
              });
            })()}
                <Text
                  position={[
                    sezionePilastro.larghezza / 2 - 0.3,
                    sezionePilastro.altezza / 2,
                    altezzaPilastro + 0.3
                  ]}
                  fontSize={0.25}
                  color="#000000"
                  anchorX="center"
                  anchorY="middle"
                >
                  H4
                </Text>
                {/* H3 - Angolo A-D (Fronte-Destra) */}
                <Text
                  position={[
                    larghezzaSoletta - sezionePilastro.larghezza / 2 + 0.3,
                    sezionePilastro.altezza / 2 ,
                    altezzaPilastro + 0.3
                  ]}
                  fontSize={0.25}
                  color="#000000"
                  anchorX="center"
                  anchorY="middle"
                >
                  H3
                </Text>

                <Text
                  position={[
                    larghezzaSoletta - sezionePilastro.larghezza / 2 + 0.3,
                    lunghezzaSoletta - sezionePilastro.altezza / 2,
                    altezzaPilastro + 0.3
                  ]}
                  fontSize={0.25}
                  color="#000000"
                  anchorX="center"
                  anchorY="middle"
                >
                  H2
                </Text>
                <Text
                  position={[
                    sezionePilastro.larghezza / 2 - 0.3,
                    lunghezzaSoletta - sezionePilastro.altezza / 2,
                    altezzaPilastro + 0.3
                  ]}
                  fontSize={0.25}
                  color="#000000"
                  anchorX="center"
                  anchorY="middle"
                >
                  H1
                </Text>
        {(isLibero("Fronte") && isLibero("Retro") || isLibero("Fronte") || isLibero("Retro")) && (<>
          {/* H4 - Fronte Sinistra */}
          {(isLibero("Sinistra") && isLibero("Fronte")) && (
            <PilastroCliccabile
              id="pilastro_H4"
              materialF={textureFioriere ?? fallbackTexture}
              altezza={altezzaPilastro - muroAngoloAB}
              cs={{
                org: {
                  x: sezionePilastro.larghezza / 2,
                  y: sezionePilastro.altezza / 2,
                  z: altezzaPilastro / 2 + muroAngoloAB / 2,
                },
                direction: [0, 0, 1],
              }}
              material={texture}
              isSelected={fioriereAttuali.some(f => f.direzione === "pilastro_H4")}
              isHovered={pilastroHover === "pilastro_H4"}
              onToggleSelect={(id) =>
                setPilastriSelezionati((prev) => {
                  const nuovo = new Set(prev);
                  nuovo.has(id) ? nuovo.delete(id) : nuovo.add(id);
                  return nuovo;
                })
              }
              onHover={setPilastroHover}
            />
          )}

          {(isLibero("Destra") && isLibero("Fronte")) && (
          <PilastroCliccabile
            id="pilastro_H3"
            materialF={textureFioriere ?? fallbackTexture}
            altezza={altezzaPilastro - muroAngoloAD}
            cs={{
              org: {
                x: larghezzaSoletta - sezionePilastro.larghezza / 2,
                y: sezionePilastro.altezza / 2,
                z: altezzaPilastro / 2 + muroAngoloAD / 2,
              },
              direction: [0, 0, 1],
            }}
            material={texture}
            isSelected={fioriereAttuali.some(f => f.direzione === "pilastro_H3")}
            isHovered={pilastroHover === "pilastro_H3"}
            onToggleSelect={(id) =>
              setPilastriSelezionati((prev) => {
                const nuovo = new Set(prev);
                nuovo.has(id) ? nuovo.delete(id) : nuovo.add(id);
                return nuovo;
              })
            }
            onHover={setPilastroHover}
          />
          )}

          {(isLibero("Destra") && isLibero("Retro")) && (
            <PilastroCliccabile
              id="pilastro_H2"
              materialF={textureFioriere ?? fallbackTexture}
              altezza={altezzaPilastro - muroAngoloCD}
              cs={{
                org: {
                  x: larghezzaSoletta - sezionePilastro.larghezza / 2,
                  y: lunghezzaSoletta - sezionePilastro.altezza / 2,
                  z: altezzaPilastro / 2 + muroAngoloCD / 2,
                },
                direction: [0, 0, 1],
              }}
              material={texture}
              isSelected={fioriereAttuali.some(f => f.direzione === "pilastro_H2")}
              isHovered={pilastroHover === "pilastro_H2"}
              onToggleSelect={(id) =>
                setPilastriSelezionati((prev) => {
                  const nuovo = new Set(prev);
                  nuovo.has(id) ? nuovo.delete(id) : nuovo.add(id);
                  return nuovo;
                })
              }
              onHover={setPilastroHover}
            />
          )}

          {(isLibero("Sinistra") && isLibero("Retro")) && (
            <PilastroCliccabile
              id="pilastro_H1"
              materialF={textureFioriere ?? fallbackTexture}
              altezza={altezzaPilastro - muroAngoloBC}
              cs={{
                org: {
                  x: sezionePilastro.larghezza / 2,
                  y: lunghezzaSoletta - sezionePilastro.altezza / 2,
                  z: altezzaPilastro / 2 + muroAngoloBC / 2,
                },
                direction: [0, 0, 1],
              }}
              material={texture}
              isSelected={fioriereAttuali.some(f => f.direzione ===  "pilastro_H1")}
              isHovered={pilastroHover === "pilastro_H1"}
              onToggleSelect={(id) =>
                setPilastriSelezionati((prev) => {
                  const nuovo = new Set(prev);
                  nuovo.has(id) ? nuovo.delete(id) : nuovo.add(id);
                  return nuovo;
                })
              }
              onHover={setPilastroHover}
            />
          )}</>)}
    {numeroPannelliLaterali > 0 && (
     <>
      <>
      {(numeroPannelliAD > 0) &&(
        <PannelliLaterali
          numeroPannelli={numeroPannelliAD}
          altezzaPilastro={altezzaPilastro + sezioneTrave.altezza}
          sezionePilastro={sezionePilastro}
          texture={texture}
          direzione="Destra"
          verso="positivo"
          csPilastroEsterno={{
            x: larghezzaSoletta - sezionePilastro.larghezza / 2,
            y: (!isLibero("Fronte") && !isLibero("Retro"))
              ? sezionePilastro.larghezza/2
              :sezionePilastro.larghezza + 0.03,
            z: (altezzaPilastro + sezioneTrave.altezza) / 2,
          }}
        />)}
      {(numeroPannelliAD2 > 0) &&(
        <PannelliLaterali
          numeroPannelli={numeroPannelliAD2}
          altezzaPilastro={altezzaPilastro + sezioneTrave.altezza}
          sezionePilastro={sezionePilastro}
          texture={texture}
          direzione="Destra"
          verso="negativo"
          csPilastroEsterno={{
            x: larghezzaSoletta - sezionePilastro.larghezza / 2,
            y: installazione == InstallationType.addossata_larghezza || installazione == InstallationType.patio
              ? lunghezzaSoletta
              : lunghezzaSoletta - sezionePilastro.larghezza + 0.03,
            z: (altezzaPilastro + sezioneTrave.altezza) / 2,
          }}
        />)}
      </>
    {(installazione !== InstallationType.addossata_profondita &&
   installazione !== InstallationType.angolare) && (
          <>
          {(numeroPannelliBA > 0) &&(
            <PannelliLaterali
              numeroPannelli={numeroPannelliBA}
              altezzaPilastro={altezzaPilastro + sezioneTrave.altezza}
              sezionePilastro={sezionePilastro}
              texture={texture}
              direzione="Sinistra"
              verso="positivo"
              csPilastroEsterno={{
                x: sezionePilastro.larghezza / 2,
                y: installazione == InstallationType.patio
                  ? sezionePilastro.larghezza/2
                  :sezionePilastro.larghezza + 0.03,
                z: (altezzaPilastro + sezioneTrave.altezza) / 2,
              }}
            />)}
          {(numeroPannelliBA2 > 0) &&(
            <PannelliLaterali
              numeroPannelli={numeroPannelliBA2}
              altezzaPilastro={altezzaPilastro + sezioneTrave.altezza}
              sezionePilastro={sezionePilastro}
              texture={texture}
              direzione="Sinistra"
              verso="negativo"
              csPilastroEsterno={{
                x: sezionePilastro.larghezza / 2,
                y: installazione == InstallationType.addossata_larghezza || installazione == InstallationType.patio
                  ? lunghezzaSoletta
                  : lunghezzaSoletta - sezionePilastro.larghezza + 0.03,
                z: (altezzaPilastro + sezioneTrave.altezza) / 2,
              }}
            />)}
          </>
      )}
          {(installazione !== InstallationType.addossata_larghezza && installazione !== InstallationType.patio) && (
                <>
                {(numeroPannelliCB > 0) &&(
                  <PannelliLaterali
                    numeroPannelli={numeroPannelliCB}
                    altezzaPilastro={altezzaPilastro + sezioneTrave.altezza}
                    sezionePilastro={sezionePilastro}
                    texture={texture}
                    direzione="Retro"
                    verso="positivo"
                    csPilastroEsterno={{
                      x: installazione == InstallationType.addossata_profondita
                        ?  sezionePilastro. larghezza/2
                        : sezionePilastro.larghezza + 0.03,
                      y: lunghezzaSoletta - sezionePilastro.altezza / 2,
                      z: (altezzaPilastro + sezioneTrave.altezza) / 2,
                    }}
                  />)}
                {(numeroPannelliCB2 > 0) &&(
                  <PannelliLaterali
                    numeroPannelli={numeroPannelliCB2}
                    altezzaPilastro={altezzaPilastro + sezioneTrave.altezza}
                    sezionePilastro={sezionePilastro}
                    texture={texture}
                    direzione="Retro"
                    verso="negativo"
                    csPilastroEsterno={{
                      x: larghezzaSoletta - sezionePilastro.larghezza + 0.03,
                      y: lunghezzaSoletta - sezionePilastro.altezza / 2,
                      z: (altezzaPilastro + sezioneTrave.altezza) / 2,
                    }}
                  />)}
                </>)}
              
              {(installazione !== InstallationType.patio) && (
                <>
                {(numeroPannelliAB > 0) &&(
                <PannelliLaterali
                  numeroPannelli={numeroPannelliAB}
                  altezzaPilastro={altezzaPilastro + sezioneTrave.altezza}
                  sezionePilastro={sezionePilastro}
                  texture={texture}
                  direzione="Fronte"
                  verso="positivo"
                  csPilastroEsterno={{
                    x: installazione == InstallationType.addossata_profondita
                      ?  sezionePilastro. larghezza/2
                      : sezionePilastro.larghezza + 0.03,
                    y: sezionePilastro.altezza / 2,
                    z: (altezzaPilastro + sezioneTrave.altezza) / 2,
                  }}
                />)}
                {(numeroPannelliAB2 > 0) &&(
                  <PannelliLaterali
                    numeroPannelli={numeroPannelliAB2}
                    altezzaPilastro={altezzaPilastro + sezioneTrave.altezza}
                    sezionePilastro={sezionePilastro}
                    texture={texture}
                    direzione="Fronte"
                    verso="negativo"
                    csPilastroEsterno={{
                      x: larghezzaSoletta - sezionePilastro.larghezza + 0.03,
                      y: sezionePilastro.altezza / 2,
                      z: (altezzaPilastro + sezioneTrave.altezza) / 2,
                    }}
                  />)}  
              </>)}
          </>
        )}
          {/*<Angolare
            sezionePilastro={sezionePilastro}
            sezioneTrave={sezioneTrave}
            cs={{
              org: {
                x: 0,
                y: 0,
                z: altezzaPilastro - sezioneTrave.altezza / 2,
              },
              direction: [0, 0, 1],
            }}
          />*/}
          {ledConfigurations.map(({ direzione, position, length, rotation }) => {
            const ledConfig = pergolaConfiguration.led.find((led) => led.direzione === direzione);
            const ledType = ledConfig?.tipo;
            let diff =  0;
            if(direzione === "Sinistra" || direzione === "Destra") diff -= sezioneTrave.larghezza
            return (
              <AreaIlluminazione
                key={direzione}
                position={position}
                length={length + diff}
                rotation={rotation}
                isACtive={!isDay}
                isSelected={isLedActive(direzione)}
                ledType={ledType} 
              />
            );
          })}
          <group>
          {/* Muri dinamici basati su pergolaConfiguration.muri */}
          {Array.isArray(pergolaConfiguration.muri) ? (
            pergolaConfiguration.muri.map((muro) => {
              if (!muro?.direzione) {
                console.warn("Muro senza direzione, skip:", muro);
                return null;
              }
              const direzione = muro.direzione;
              const altezzaMuroMetri = muro.altezza/1000;

              let cs: ElementCoordinateSystem = {
                org: { x: 0, y: 0, z: altezzaMuroMetri / 2 },
                direction: [0, 0, 0] as [number, number, number],
              };

              let larghezzaMuro = 0;
              let lunghezzaMuro = 0;
              switch (direzione) {
                case "Fronte":
                  larghezzaMuro = larghezzaSoletta;
                  lunghezzaMuro = sezionePilastro.larghezza;
                  cs.org = {
                    x: larghezzaSoletta / 2,
                    y: sezionePilastro.altezza / 2,
                    z: altezzaMuroMetri / 2,
                  };
                  cs.direction = [0, 0, 0];
                  break;

                case "Retro":
                  larghezzaMuro = larghezzaSoletta;
                  lunghezzaMuro = sezionePilastro.larghezza;
                  cs.org = {
                    x: larghezzaSoletta / 2,
                    y: lunghezzaSoletta - sezionePilastro.altezza / 2,
                    z: altezzaMuroMetri / 2,
                  };
                  cs.direction = [0, 0, 0];
                  break;

                case "Destra":
                  larghezzaMuro = sezionePilastro.larghezza;
                  lunghezzaMuro = lunghezzaSoletta;
                  cs.org = {
                    x: larghezzaSoletta - sezionePilastro.larghezza / 2,
                    y: lunghezzaSoletta / 2,
                    z: altezzaMuroMetri / 2,
                  };
                  cs.direction = [0, 0, 0];
                  break;

                case "Sinistra":
                  larghezzaMuro = sezionePilastro.larghezza;
                  lunghezzaMuro = lunghezzaSoletta;
                  cs.org = {
                    x: sezionePilastro.larghezza / 2,
                    y: lunghezzaSoletta / 2,
                    z: altezzaMuroMetri / 2,
                  };
                  cs.direction = [0, 0, 0];
                  break;

                default:
                  return null;
              }
              return (
                <Muro
                  key={direzione}
                  larghezza={larghezzaMuro}
                  lunghezza={lunghezzaMuro}
                  altezza={altezzaMuroMetri}
                  cs={cs}
                />
              );
            })
          ) : (
            <></>
          )}
        </group>
          {(lunghezzaSoletta >= 7) && (() => {
            const step = larghezzaSoletta;
            const stepy = lunghezzaSoletta / nstacchiy;
            const numPil = nstacchiy - 1 ;
            return Array.from({ length: numPil }, (_, i) => {
              let x = step/2 ;
              let y = 0;
              if(i === 0){
                y = 0 + stepy;
              }else if(i === numPil - 1 && (larghezzaSoletta - (numPil - 2)*step < 2)){
                return null
              }else{
                y = 0 + (stepy * (i+1));
              }
              {/*TRAVE IN COMUNE TRA MODULI PROFONDITA*/}
              return (
                <Trave
                  sezione={sezioneTrave}
                  lunghezza={step}
                  material={texture}
                  cs={{
                    org: {
                      x,
                      y,
                      z: altezzaPilastro,
                    },
                    direction: [1, 0, 0],
                  }}
                />
              );
            }).filter(Boolean);
          })()}
          {lunghezzaSoletta >= 7 && (() => {
            const step = lunghezzaSoletta / nstacchiy;
            const numPil = Math.floor(lunghezzaSoletta / step) + 1;
            return Array.from({ length: numPil }, (_, i) => {
              let y = 0;
              let lunghezza = 0;
              if(i === 0){
                y = 0 + step/2 + sezioneTrave.larghezza - sezioneTrave.larghezza + sezioneTrave.larghezza/4;
                lunghezza = step - sezioneTrave.larghezza*1.5;
              }else if(i === numPil-1 && (lunghezzaSoletta - (numPil - 1)*step < 2)){
                return null;
              }else if(i === numPil - 2){
                lunghezza = lunghezzaSoletta - (step*i) - sezioneTrave.larghezza*1.5;
                y = 0 + lunghezza/2 + sezioneTrave.larghezza/2 + (i * step);
              }else{
                y = 0 + step/2 + (i * step);
                lunghezza = step - sezioneTrave.larghezza;
              }
              {/*TRAVE SINISTRA*/}
              return (<>
                <Trave
                  sezione={sezioneTrave}
                  lunghezza={lunghezza} 
                  material={texture}
                  cs={{
                    org: {
                      x: sezioneTrave.larghezza/2,
                      y,
                      z: altezzaPilastro,
                    },
                    direction: [0, 1, 0],
                  }}
                />
                <Trave
                  sezione={sezioneTrave}
                  lunghezza={lunghezza}
                  material={texture}
                  cs={{
                    org: {
                      x: larghezzaSoletta - sezioneTrave.larghezza/2,
                      y,
                      z: altezzaPilastro,
                    },
                    direction: [0, 1, 0],
                  }}
                /></>
              );
            }).filter(Boolean);
          })()}
          { larghezzaSoletta <= 6.1 && (() => {
            {/*TRAVE FRONTE RETRO*/}
            return <>
              <Trave
                sezione={sezioneTrave}
                lunghezza={larghezzaSoletta}
                material={texture}
                cs={{
                  org: {
                    x: larghezzaSoletta / 2,
                    y: lunghezzaSoletta - sezioneTrave.larghezza / 2,
                    z: altezzaPilastro,
                  },
                  direction: [1, 0, 0],
                }}
              />
               <Trave
                sezione={sezioneTrave}
                lunghezza={larghezzaSoletta}
                material={texture}
                cs={{
                  org: {
                    x: larghezzaSoletta / 2,
                    y: sezioneTrave.larghezza / 2,
                    z: altezzaPilastro,
                  },
                  direction: [1, 0, 0],
                }}
              />
            </>
          })()}
          { lunghezzaSoletta < 7 && <>
                {/*TRAVE DI Sinistra*/}
                <Trave
                  sezione={sezioneTrave}
                  lunghezza={lunghezzaSoletta - sezioneTrave.larghezza*2}
                  material={texture}
                  cs={{
                    org: {
                      x: 0 + sezioneTrave.larghezza / 2,
                      y: lunghezzaSoletta / 2,
                      z: altezzaPilastro,
                    },
                    direction: [0, 1, 0],
                  }}
                />
                {/*TRAVE DI Destra*/}
                <Trave
                  sezione={sezioneTrave}
                  lunghezza={lunghezzaSoletta - sezioneTrave.larghezza*2}
                  material={texture}
                  cs={{
                    org: {
                      x: larghezzaSoletta - sezioneTrave.larghezza / 2,
                      y: lunghezzaSoletta / 2,
                      z: altezzaPilastro,
                    },
                    direction: [0, 1, 0],
                  }}
                />
          </>
          }
            { larghezzaSoletta >=4.1 && (() => {
              const step = larghezzaSoletta / nstacchi;
              const numPil = Math.floor(larghezzaSoletta / step)
              return Array.from({ length: numPil }, (_, i) => {
                let y = 0;
                let x = sezioneTrave.larghezza/2;
                let lunghezza = 0;
                if(i === 0){
                  y = lunghezzaSoletta - lunghezzaSoletta/2;
                  x = larghezzaSoletta - ( larghezzaSoletta - (numPil)*step) - sezioneTrave.larghezza/2;
                  lunghezza = lunghezzaSoletta - sezioneTrave.larghezza*2;
                }else if(i === numPil-1 && (larghezzaSoletta - (numPil-1)*step < 2)){
                  return null;
                }else if(i === numPil-1){
                  lunghezza = lunghezzaSoletta - sezioneTrave.larghezza*2;
                  y = lunghezzaSoletta - lunghezzaSoletta/2;
                  x = larghezzaSoletta - ( larghezzaSoletta - (numPil - i)*step) - sezioneTrave.larghezza/2;
                }else{
                  lunghezza = lunghezzaSoletta - sezioneTrave.larghezza + sezionePilastro.larghezza/2 ;
                  y = lunghezzaSoletta - lunghezzaSoletta/2;
                  x = larghezzaSoletta - ( larghezzaSoletta - (numPil - i)*step) - sezioneTrave.larghezza/2;
                }
                {/*TRAVE IN COMUNE TRA MODULI LARGHEZZA*/}
                return (
                  <Trave
                  sezione={sezioneTrave}
                  lunghezza={lunghezza}
                  material={texture}
                  cs={{
                    org: {
                      x,
                      y,
                      z: altezzaPilastro,
                    },
                    direction: [0, 1, 0],
                  }}
                />
                );
              }).filter(Boolean);
            })()}
          
        {larghezzaSoletta >= 4.1 && (() => {
          const step = larghezzaSoletta / nstacchi;
          const numTravi = Math.floor(larghezzaSoletta / step) + 1; 
          const travi = [];

          for (let i = 0; i < numTravi; i++) {
            let x, lunghezza;

            if (i === 0) {
              x = step / 2;
              lunghezza = step;
            } else if (i === numTravi - 1) {
              const inizioUltima = (numTravi - 1) * step;
              const fineUltima = larghezzaSoletta;
              lunghezza = fineUltima - inizioUltima;
              x = inizioUltima + lunghezza / 2 ;
            } else {
              x = i * step + step / 2;
              lunghezza = step;
            }
            {/*TRAVE RETRO*/}
            travi.push(
              <Trave
                key={`trave-orizzontale-${i}`}
                sezione={sezioneTrave}
                lunghezza={lunghezza}
                material={texture}
                cs={{
                  org: {
                    x,
                    y: lunghezzaSoletta - sezioneTrave.larghezza / 2,
                    z: altezzaPilastro,
                  },
                  direction: [1, 0, 0],
                }}
              />
            );
          }

          return travi;
        })()}
        {larghezzaSoletta >= 4.1 && (() => {
          const step = larghezzaSoletta / nstacchi;
          const numTravi = Math.floor(larghezzaSoletta / step) + 1; 
          const travi = [];

          for (let i = 0; i < numTravi; i++) {
            let x, lunghezza;

            if (i === 0) {
              x = step / 2;
              lunghezza = step;
            } else if (i === numTravi - 1) {
              const inizioUltima = (numTravi - 1) * step;
              const fineUltima = larghezzaSoletta;
              lunghezza = fineUltima - inizioUltima;
              x = inizioUltima + lunghezza / 2 ;
            } else {
              x = i * step + step / 2;
              lunghezza = step;
            }
            {/*TRAVE FRONTE*/}
            travi.push(
              <Trave
                key={`trave-orizzontale-${i}`}
                sezione={sezioneTrave}
                lunghezza={lunghezza}
                material={texture}
                cs={{
                  org: {
                    x,
                    y: sezioneTrave.larghezza / 2,
                    z: altezzaPilastro,
                  },
                  direction: [1, 0, 0],
                }}
              />
            );
          }

          return travi;
        })()}
        {!stacco && <>
          {residuoLamelle > 0 && (
            orientamentoLamelle === "Larghezza" ? (
              <ScartoDe
                material={textureLamelle}
                sezione={sezioneLamelle}
                scarto={residuoLamelle}
                lunghezza={larghezzaSoletta - (sezioneTrave.larghezza * 2)}
                cs={{
                  org: {
                    x: sezioneTrave.larghezza,
                    y: sezioneTrave.larghezza + residuoLamelle / 2 + 0.01,
                    z: altezzaPilastro + sezioneTrave.altezza - 0.015,
                  },
                  direction: [1, 0, 0],
                }}
              />):(
                <ScartoDeLarghezza
                  material={textureLamelle}
                  sezione={sezioneLamelle}
                  scarto={residuoLamelle}
                  lunghezza={lunghezzaSoletta - (sezioneTrave.larghezza * 2)}
                  cs={{
                    org: {
                      x: larghezzaSoletta - sezioneTrave.larghezza - 0.04,
                      y: sezioneTrave.larghezza,
                      z: altezzaPilastro + sezioneTrave.altezza - 0.015,
                    },
                    direction: [0, 1, 0],
                  }}/>
              )
          )}</>}
          {stacco && residuoLamelle > 0 && (
              <>
                {orientamentoLamelle === "Larghezza" ? (
                  Array.from({ length: nstacchiy }).map((_, i) => (
                    <ScartoDe
                      key={`scarto-y-${i}`}
                      material={textureLamelle}
                      sezione={sezioneLamelle}
                      scarto={residuoLamelle}
                      lunghezza={larghezzaSoletta - (sezioneTrave.larghezza * 2)}
                      cs={{
                        org: {
                          x: sezioneTrave.larghezza,
                          y:
                            sezioneTrave.larghezza +
                            residuoLamelle / 2 +
                            0.01 +
                            (sezioneTrave.larghezza + (0.17 * numeroLamiere) / nstacchiy) * i,
                          z: altezzaPilastro + sezioneTrave.altezza - 0.015,
                        },
                        direction: [1, 0, 0],
                      }}
                    />
                  ))
                ) : (
                  <ScartoDeLarghezza
                    key="scarto-larghezza"
                    material={textureLamelle}
                    sezione={sezioneLamelle}
                    scarto={residuoLamelle}
                    lunghezza={lunghezzaSoletta - (sezioneTrave.larghezza * 2)}
                    cs={{
                      org: {
                        x: larghezzaSoletta - sezioneTrave.larghezza - 0.04,
                        y: sezioneTrave.larghezza,
                        z: altezzaPilastro + sezioneTrave.altezza - 0.015,
                      },
                      direction: [0, 1, 0],
                    }}
                  />
                )}
              </>
            )}
          <ExtraTravi
            needExtraTrave={needTrave}
            sezionePilastro={sezionePilastro}
            larghezzaSoletta={larghezzaSoletta}
            lunghezzaSoletta={lunghezzaSoletta}
            altezzaPilastro={altezzaPilastro}
            texture={texture}
            isLatoLibero={isLibero}
            installazione={installazione}
          />
          {(pergolaConfiguration.lati ?? []).map((lato) => {
            if (!lato || !lato.tipoLato || !lato.tipoLato.tipologia) return null;
            const muro = pergolaConfiguration.muri.find(m => m.direzione === lato.direzione);
            const altezza_muro = muro ? muro.altezza / 1000 : 0;
            const tipologia = lato.tipoLato.tipologia;
            const tipoChiusura = tipologia.tipo_chiusura;
            const identificativo = lato.direzione + lato.segmento; 
            const textureTeloLato = textureTeliMap[identificativo] || textureTelo;
            const isCristalLato = ('tipo_telo' in tipologia && tipologia.tipo_telo === TeloType.teloCristal) ||
                                  ('codice_telo' in tipologia && tipologia.codice_telo === "2020");
            const nmuero = lato.segmento-1;
            const segmentiLiberi = getPilastriIntermediPerLato(lato.direzione).filter((_, index) => [nmuero, nmuero].includes(index));
            const commonProps = {
              key: lato.direzione + lato.segmento,
              lunghezzaSoletta,
              larghezzaSoletta,
              altezzaPilastro,
              sezionePilastro,
              sezioneTrave,
              texture,
              lato: lato.direzione,
              needExtra: needTrave,
              muro: altezza_muro,
              segmentiLiberi,
            };
            const stacchiAttuali = (lato.direzione === 'Destra' || lato.direzione === 'Sinistra') ? nstacchiy: nstacchi;
            if (tipoChiusura === ChiusuraType.vetrate_ad_impacchettamento_andromeda) {
              return <CompleteVetrataAndromeda {...commonProps} lato={lato} isOpen={statoVetrata} />;
            }

            if (tipoChiusura === ChiusuraType.vetrate_scorrevoli_maia) {
              return <CompleteVetrataMaia {...commonProps} lato={lato} isOpen={statoVetrata} />;
            }

            if (tipoChiusura === ChiusuraType.tende_zip) {
              return (
                <CompleteTenda
                  {...commonProps}
                  textureTelo={textureTeloLato}
                  statoTenda={statoTenda}
                  lato={lato}
                  isCristal={isCristalLato}
                  nstacchi={stacchiAttuali}
                />
              );
            }

            return null;
        })}
        {!stacco && <>
          {[...Array(numeroLamiere)].map((_, i) => {
          const zPos = altezzaPilastro + sezioneTrave.altezza - 0.02; 
          return orientamentoLamelle === "Larghezza" ? (
            <Prototipo
              key={`lamella-larghezza-${i}`}
              material={textureLamelle}
              sezione={sezioneLamelle}
              lunghezza={larghezzaSoletta - (sezioneTrave.larghezza * 2)}
              cs={{
                org: {
                  x: sezioneTrave.larghezza,
                  y:
                    i === 0
                      ? sezioneTrave.larghezza * 2 + residuoLamelle
                      : sezioneTrave.larghezza * 2 + (residuoLamelle + 0.17 * i),
                  z: zPos,
                },
                direction: [1, 0, 0],
              }}
              rotazione={rotazioneLamelle(140)}
            />
          ) : (
            <PrototipoProfondita
              key={`lamella-profondita-${i}`}
              material={textureLamelle}
              sezione={sezioneLamelle}
              lunghezza={lunghezzaSoletta - (sezioneTrave.larghezza * 2)}
              cs={{
                org: {
                  x: i === 0
                    ? sezioneTrave.larghezza * 2
                    : sezioneTrave.larghezza * 2 + (0.17 * i),
                  y: sezioneTrave.larghezza,
                  z: zPos,
                },
                direction: [1, 0, 0],
              }}
              rotazione={rotazioneLamelle(140)}
            />
          );
        })}</>}
        {stacco && (
          <>
            {orientamentoLamelle === "Larghezza" ? (
              Array.from({ length: Math.floor(nstacchi) }, (_, i2) => {
                const zPos = altezzaPilastro + sezioneTrave.altezza - 0.02;
                const step = larghezzaSoletta / nstacchi;

                const offsetX = i2 === 1
                  ? step - sezioneTrave.larghezza
                  : (i2 % 2 === 0)
                    ? step - sezioneTrave.larghezza / 2
                    : step - sezioneTrave.larghezza / 2 + 0.015;

                const lunghezzaDisponibile = larghezzaSoletta - step * i2;
                const larg = lunghezzaDisponibile > step ? step : lunghezzaDisponibile;
                const sott = i2 === 0 ? sezioneTrave.larghezza * 2 : sezioneTrave.larghezza;
                const lunghezzaLamella = larg - sott;
                const lungPassoY = lunghezzaSoletta - step * i2;
                //DA CORREGGERE MODULI CON STACCO PER PROFONDITà
                const cicliPerFila = Math.round(numeroLamiereCalcolato / 1);
                const cicli = nstacchiy >= 2 && (lunghezzaSoletta >= 9 && lunghezzaSoletta < 14.2)? cicliPerFila : cicliPerFila;

                return (
                  <group key={`fila-larghezza-${i2}`}>
                    {Array.from({ length: 1 }, (_, i3) => (
                      <group key={`sottoriga-${i3}`}>
                        {Array.from({ length: cicli }, (_, i) => {
                          if(i3 === nstacchiy-1 && i === cicli-1) return;
                          const yPos =
                            i === 0 && i3 === 0
                              ? sezioneTrave.larghezza * 2 + residuoLamelle +
                                (sezioneTrave.larghezza * 2 + residuoLamelle + 0.17 * (cicli - 1)) * i3
                              : sezioneTrave.larghezza * 2 +
                                (residuoLamelle  + 0.17 * i) +
                                (sezioneTrave.larghezza * 2 + residuoLamelle + 0.173 * (cicli - 1)) * i3;

                          const xPos = sezioneTrave.larghezza + offsetX * i2;

                          return (
                            <Prototipo
                              key={`lamella-larghezza-${i2}-${i3}-${i}`}
                              material={textureLamelle}
                              sezione={sezioneLamelle}
                              lunghezza={lunghezzaLamella}
                              cs={{
                                org: { x: xPos, y: yPos, z: zPos },
                                direction: [1, 0, 0],
                              }}
                              rotazione={rotazioneLamelle(140)}
                            />
                          );
                        })}
                      </group>
                    ))}
                  </group>
                );
              })
            ) : (
              Array.from({ length: Math.floor(nstacchi) }, (_, i2) => {
                const zPos = altezzaPilastro + sezioneTrave.altezza - 0.02;
                const step = larghezzaSoletta/nstacchi;
                const offsetX = i2 === 1
                  ? step - sezioneTrave.larghezza
                  : (i2 % 2 === 0)
                    ? step - sezioneTrave.larghezza / 2
                    : step - sezioneTrave.larghezza / 2 + 0.015;
                const lunghezzaLamella = (lunghezzaSoletta / nstacchiy) - sezioneTrave.larghezza * 2 + 0.04;

                const cicliX = Math.round(numeroLamiereCalcolato / nstacchi);
                const cicli = nstacchi >= 2 ? cicliX - 1 : cicliX;

                return (
                  <group key={`fila-profondita-${i2}`}>
                    {Array.from({ length: nstacchiy }, (_, i3) => {
                      const stepy = lunghezzaSoletta / nstacchiy;
                      const yBase = sezioneTrave.larghezza;
                      const yOffset = i3 > 0 
                        ? stepy*i3 - sezioneTrave.larghezza/2
                        : 0;

                      return (
                        <group key={`riga-profondita-${i3}`}>
                          {Array.from({ length: cicli }, (_, i) => {
                            const xPos = i === 0
                              ? sezioneTrave.larghezza * 2
                              : sezioneTrave.larghezza * 2 + 0.17 * i;

                            const yPos = yBase + yOffset;

                            return (
                              <PrototipoProfondita
                                key={`lamella-profondita-${i2}-${i3}-${i}`}
                                material={textureLamelle}
                                sezione={sezioneLamelle}
                                lunghezza={lunghezzaLamella}
                                cs={{
                                  org: { x: xPos + offsetX * i2, y: yPos, z: zPos },
                                  direction: [1, 0, 0],
                                }}
                                rotazione={rotazioneLamelle(140)}
                              />
                            );
                          })}
                        </group>
                      );
                    })}
                  </group>
                );
              })
            )}
          </>
        )}
        </group>
        <InstallationWalls
          installazione={installazione}
          larghezzaSoletta={larghezzaSoletta}
          isLatoLibero={isLibero}
          lunghezzaSoletta={lunghezzaSoletta}
          altezzaMuro={altezzaMuro}
        />
      </Canvas>
    </div>
  );
}

export default PergolaFinal;